/**
 * First-turn kickoff for Wave B Update Work Spec agent sessions.
 *
 * Ingest creates a seeded conversation then relies on the chat pane to start
 * chat-stream. Fire-and-forget `chat-agentic-continue` dropped when the agent
 * panel had not bound the new thread yet (prod 2026-08-25). Queue until the
 * matching pane hydrates the seed, then flush `agenticContinue`.
 */

import { toast } from 'sonner'

export const UPDATE_WORK_SPEC_KICKOFF_EVENT = 'update-work-spec-kickoff'
export const UPDATE_WORK_SPEC_KICKOFF_TIMEOUT_MS = 2500

export type UpdateWorkSpecKickoffDetail = {
  targetConversationId: string
  targetThreadId?: string
  documentId?: string
  skillId?: string
}

type PendingKickoff = UpdateWorkSpecKickoffDetail & {
  enqueuedAt: number
}

let pending: PendingKickoff | null = null
let timeoutId: ReturnType<typeof setTimeout> | null = null
const startedConversationIds = new Set<string>()
const toastedConversationIds = new Set<string>()

export function shouldKickoffSeededUpdateWorkSpec(opts: {
  source?: unknown
  messages: Array<{ role?: string }>
}): boolean {
  if (opts.source !== 'update_work_spec_agent') return false
  const hasUser = opts.messages.some((m) => m.role === 'user')
  const hasAssistant = opts.messages.some((m) => m.role === 'assistant')
  return hasUser && !hasAssistant
}

export function wasUpdateWorkSpecKickoffStarted(conversationId: string | null | undefined): boolean {
  if (!conversationId) return false
  return startedConversationIds.has(conversationId)
}

function clearTimeoutIfAny() {
  if (timeoutId != null) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
}

function armKickoffTimeout(conversationId: string) {
  clearTimeoutIfAny()
  timeoutId = setTimeout(() => {
    timeoutId = null
    if (startedConversationIds.has(conversationId)) return
    if (pending?.targetConversationId !== conversationId) return
    if (toastedConversationIds.has(conversationId)) return
    toastedConversationIds.add(conversationId)
    toast.error('Update Work Spec did not start', {
      description: 'The agent chat opened but the stream never began. Try Update Work Spec again.',
      duration: 8000,
    })
  }, UPDATE_WORK_SPEC_KICKOFF_TIMEOUT_MS)
}

function dispatchKickoffEvent(detail: UpdateWorkSpecKickoffDetail) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(UPDATE_WORK_SPEC_KICKOFF_EVENT, { detail }))
}

/** Enqueue (or merge) a first-turn kickoff. Idempotent per conversation. */
export function enqueueUpdateWorkSpecKickoff(detail: {
  targetConversationId?: string | null
  targetThreadId?: string | null
  documentId?: string | null
  skillId?: string | null
}): void {
  const conversationId = detail.targetConversationId ? String(detail.targetConversationId) : ''
  if (!conversationId) return
  if (startedConversationIds.has(conversationId)) return

  const threadId = detail.targetThreadId ? String(detail.targetThreadId) : undefined
  const documentId = detail.documentId ? String(detail.documentId) : undefined
  const skillId = detail.skillId ? String(detail.skillId) : 'basecamp-todo'

  if (pending?.targetConversationId === conversationId) {
    pending = {
      ...pending,
      targetThreadId: threadId || pending.targetThreadId,
      documentId: documentId || pending.documentId,
      skillId: skillId || pending.skillId,
    }
    dispatchKickoffEvent(pending)
    return
  }

  pending = {
    targetConversationId: conversationId,
    targetThreadId: threadId,
    documentId,
    skillId,
    enqueuedAt: Date.now(),
  }
  armKickoffTimeout(conversationId)
  dispatchKickoffEvent(pending)
}

export function peekUpdateWorkSpecKickoff(): PendingKickoff | null {
  return pending
}

/**
 * If this pane is bound to the pending session and the seed has hydrated
 * (user message, no assistant), consume and return the detail so the pane
 * can `handleSend(..., 'continue')`.
 */
export function consumeUpdateWorkSpecKickoffIfReady(opts: {
  hostedThreadId?: string | null
  conversationId?: string | null
  messages: Array<{ role?: string }>
  isStreaming: boolean
}): UpdateWorkSpecKickoffDetail | null {
  if (!pending) return null
  const conversationId = pending.targetConversationId
  if (startedConversationIds.has(conversationId)) {
    pending = null
    clearTimeoutIfAny()
    return null
  }
  if (pending.targetThreadId) {
    if (!opts.hostedThreadId || opts.hostedThreadId !== pending.targetThreadId) {
      return null
    }
  }
  if (!opts.conversationId || opts.conversationId !== conversationId) {
    return null
  }
  // A live stream on this conversation means the kickoff already began.
  // Do not toast "stream never began" while the composer is queueing.
  if (opts.isStreaming) {
    startedConversationIds.add(conversationId)
    pending = null
    clearTimeoutIfAny()
    return null
  }
  if (opts.messages.length === 0) return null

  const hasAssistant = opts.messages.some((m) => m.role === 'assistant')
  if (hasAssistant) {
    startedConversationIds.add(conversationId)
    pending = null
    clearTimeoutIfAny()
    return null
  }

  const hasUser = opts.messages.some((m) => m.role === 'user')
  if (!hasUser) return null

  const taken: UpdateWorkSpecKickoffDetail = {
    targetConversationId: pending.targetConversationId,
    targetThreadId: pending.targetThreadId,
    documentId: pending.documentId,
    skillId: pending.skillId,
  }
  startedConversationIds.add(conversationId)
  pending = null
  clearTimeoutIfAny()
  return taken
}

/** Test-only: reset module state. */
export function resetUpdateWorkSpecKickoffForTests(): void {
  pending = null
  clearTimeoutIfAny()
  startedConversationIds.clear()
  toastedConversationIds.clear()
}
